import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { combineLatest, debounceTime, startWith } from 'rxjs';
import { Revisione53State } from '../../../../store/revisione53.reducers';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { MyVerbale } from '../../../../models/interfaces/revisione.interface';
import { cloneDeep } from 'lodash-es';
import { getFormArray } from '@shared/helpers/form.helper';
import { withInitialValueDebounced } from '@shared/operators/with-initial-value-debounced.operator';
import { withChangeDebounced } from '@shared/operators/with-change-debounced.operator';
import { Utilities } from '@core/helpers/utilities';
import {
    controlloEventualiDiffida30PercentoSvant,
    controlloEventualiDiffida50PercentoVolontari,
    getSchedaCoopSocialiControl,
    getSchedaCoopSocialiGroup,
} from '../validators/helpers/scheda-coop-sociali-form-validators.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { toNum } from '@shared/helpers/math.helper';

export const setSchedaCoopSocialiLavoratoriMiscListeners = (form: FormGroup, store: Store<Revisione53State>): void => {
    my_NormoDotateA(form, store);
    my_NormoDotateB(form, store);
    my_PersSvantagg(form, store);
    my_ScSocObbl(form, store);
    my_calcoloPlurimo(form, store);
    my_NumConvEnti(form, store);
};

/** my_NormodotateA/B e my_PersSvantagg non sono calculationField: InfoPath ricalcola solo su OnAfterChange, mai al caricamento. */
const my_NormoDotateA = (form: FormGroup, store: Store<Revisione53State>): void => {
    const control = getSchedaCoopSocialiControl(form, 'my_NormodotateA');
    control.valueChanges.pipe(withChangeDebounced(control)).subscribe(() => {
        ricalcoloTotLavoratori(store);
    });
};

const my_NormoDotateB = (form: FormGroup, store: Store<Revisione53State>): void => {
    const control = getSchedaCoopSocialiControl(form, 'my_NormodotateB');
    control.valueChanges.pipe(withChangeDebounced(control)).subscribe(() => {
        ricalcoloTotLavoratori(store);
        ricalcoloPercentPersSvantagg(form, store);
    });
};

const my_PersSvantagg = (form: FormGroup, store: Store<Revisione53State>): void => {
    const control = getSchedaCoopSocialiControl(form, 'my_PersSvantagg');
    control.valueChanges.pipe(withChangeDebounced(control)).subscribe(() => {
        ricalcoloTotLavoratori(store);
        ricalcoloPercentPersSvantagg(form, store);
    });
};

const my_ScSocObbl = (form: FormGroup, store: Store<Revisione53State>): void => {
    const control = getSchedaCoopSocialiControl(form, 'my_ScSocObbl');
    control.valueChanges.pipe(withInitialValueDebounced(control)).subscribe(() => {
        controlloEventualiDiffida30PercentoSvant(store);
        controlloEventualiDiffida50PercentoVolontari(store);
    });
};

/** CkPlurimo = 1 se CkServSocioSanit = "1" AND almeno uno tra CkAgricolo, CkIndustr, CkComm, CkServiz = "1" */
const my_calcoloPlurimo = (form: FormGroup, store: Store<Revisione53State>): void => {
    const ctrl = (field: string) => getSchedaCoopSocialiControl(form, field);

    combineLatest([
        ctrl('my_CkServSocioSanit').valueChanges.pipe(startWith(ctrl('my_CkServSocioSanit').value)),
        ctrl('my_CkAgricolo').valueChanges.pipe(startWith(ctrl('my_CkAgricolo').value)),
        ctrl('my_CkIndustr').valueChanges.pipe(startWith(ctrl('my_CkIndustr').value)),
        ctrl('my_CkComm').valueChanges.pipe(startWith(ctrl('my_CkComm').value)),
        ctrl('my_CkServiz').valueChanges.pipe(startWith(ctrl('my_CkServiz').value)),
    ])
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(([ckServSocioSanit, ckAgricolo, ckIndustr, ckComm, ckServiz]) => {
            const isPlurimo = ckServSocioSanit === '1' && (ckAgricolo === '1' || ckIndustr === '1' || ckComm === '1' || ckServiz === '1');

            const newValue = isPlurimo ? '1' : '0';
            ctrl('my_CkPlurimo').patchValue(newValue, { emitEvent: false });

            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                myVerbaleClone.my_SezioneI__Rilevazione.my_SchedaCoopSociali.my_CkPlurimo = newValue;
                return myVerbaleClone;
            });
        });
};

const my_NumConvEnti = (form: FormGroup, store: Store<Revisione53State>): void => {
    const my_NumConvEntiControl = getSchedaCoopSocialiControl(form, 'my_NumConvEnti');
    const my_ElEntiConvFormArray = getFormArray(getSchedaCoopSocialiGroup(form), 'my_EntiConv.my_ElEntiConv');

    my_ElEntiConvFormArray.valueChanges.pipe(startWith(my_ElEntiConvFormArray.value), debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME)).subscribe(() => {
        const value = (my_ElEntiConvFormArray?.length || 0).toString(10);

        my_NumConvEntiControl.patchValue(value, { emitEvent: false });

        updateVerbaleOnStore(store, (myVerbale) => {
            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
            myVerbaleClone.my_SezioneI__Rilevazione.my_SchedaCoopSociali.my_NumConvEnti = value;
            return myVerbaleClone;
        });
    });
};

const ricalcoloTotLavoratori = (store: Store) => {
    updateVerbaleOnStore(store, (myVerbale) => {
        const myVerbaleClone = cloneDeep(myVerbale);
        const soc = myVerbaleClone.my_SezioneI__Rilevazione.my_SchedaCoopSociali;

        if (isNilOrEmpty(soc.my_NormodotateA) || isNilOrEmpty(soc.my_NormodotateB) || isNilOrEmpty(soc.my_PersSvantagg)) {
            soc.my_TotLavoratori = '';
        } else {
            soc.my_TotLavoratori = Math.round(toNum(soc.my_NormodotateA)) + Math.round(toNum(soc.my_NormodotateB)) + Math.round(toNum(soc.my_PersSvantagg));
        }

        return myVerbaleClone;
    });
};

/** Come script.vbs: B o Pers non numerici → vuoto; B = 0 → 1 se Pers > 0, altrimenti vuoto; altrimenti Pers / B. */
const calcolaPercentPersSvantagg = (normodotateB: unknown, persSvantagg: unknown): number | '' => {
    if (isNilOrEmpty(normodotateB) || isNilOrEmpty(persSvantagg)) return '';
    if (toNum(normodotateB) === 0) return toNum(persSvantagg) > 0 ? 1 : '';
    return toNum(persSvantagg) / toNum(normodotateB);
};

const ricalcoloPercentPersSvantagg = (form: FormGroup, store: Store<Revisione53State>) => {
    const percent = calcolaPercentPersSvantagg(getSchedaCoopSocialiControl(form, 'my_NormodotateB').value, getSchedaCoopSocialiControl(form, 'my_PersSvantagg').value);

    getSchedaCoopSocialiControl(form, 'my_PercentPersSvantagg').setValue(percent);

    updateVerbaleOnStore(store, (myVerbale) => {
        const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
        myVerbaleClone.my_SezioneI__Rilevazione.my_SchedaCoopSociali.my_PercentPersSvantagg = percent as string;
        return myVerbaleClone;
    });
};
